if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.upsellDialog = (this.closest('product-info') || document).querySelector('[data-add-on-upsell]');
        this.upsellBypassOnce = false;

        if (this.upsellDialog) this.setupAddOnUpsell();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (this.upsellDialog && !this.upsellBypassOnce && !this.hasSelectedInlineAddOns()) {
          this.clearAddOnSelections(this.upsellDialog);
          if (!this.upsellDialog.open) this.upsellDialog.showModal();
          return;
        }
        this.upsellBypassOnce = false;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const addOnItems = this.selectedAddOnItems();
        this.clearAddOnSelections();

        if (addOnItems.length) {
          const payload = {
            items: [this.mainCartItem(formData), ...addOnItems],
          };

          if (this.cart) {
            payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }

          config.body = JSON.stringify(payload);
        } else {
          delete config.headers['Content-Type'];

          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }

          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      setupAddOnUpsell() {
        const closeButton = this.upsellDialog.querySelector('[data-add-on-upsell-close]');
        const continueButton = this.upsellDialog.querySelector('[data-add-on-upsell-continue]');
        const skipButton = this.upsellDialog.querySelector('[data-add-on-upsell-skip]');

        closeButton?.addEventListener('click', () => this.upsellDialog.close());

        this.upsellDialog.addEventListener('click', (event) => {
          if (event.target === this.upsellDialog) this.upsellDialog.close();
        });

        continueButton?.addEventListener('click', () => this.submitAfterUpsell());

        skipButton?.addEventListener('click', () => {
          this.upsellDialog
            .querySelectorAll('[data-add-on-checkbox]:checked')
            .forEach((checkbox) => {
              checkbox.checked = false;
            });
          this.submitAfterUpsell();
        });
      }

      submitAfterUpsell() {
        this.upsellBypassOnce = true;
        this.upsellDialog.close();
        this.form.requestSubmit(this.submitButton);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      mainCartItem(formData) {
        const item = {
          id: Number(formData.get('id')),
          quantity: Number(formData.get('quantity') || 1),
        };

        const properties = {};
        formData.forEach((value, key) => {
          const match = key.match(/^properties\[(.*)\]$/);
          if (match && value !== '') properties[match[1]] = value;
        });
        if (Object.keys(properties).length) item.properties = properties;

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) item.selling_plan = sellingPlan;

        return item;
      }

      selectedAddOnItems() {
        const productInfo = this.closest('product-info') || document;
        const selectedIds = Array.from(productInfo.querySelectorAll('[data-add-on-checkbox]:checked'))
          .map((input) => Number(input.value))
          .filter(Boolean);

        return Array.from(new Set(selectedIds)).map((id) => ({ id, quantity: 1 }));
      }

      hasSelectedInlineAddOns() {
        const productInfo = this.closest('product-info') || document;
        return Boolean(productInfo.querySelector('[data-candles-toppers] [data-add-on-checkbox]:checked'));
      }

      clearAddOnSelections(root = this.closest('product-info') || document) {
        root.querySelectorAll('[data-add-on-checkbox]:checked').forEach((checkbox) => {
          checkbox.checked = false;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    }
  );
}
